import React, { useState, useEffect } from 'react';
import { Item, StoreDetails, DeliveryCharge, Discount } from '../../types';
import ItemForm from '../../components/ItemForm/ItemForm';
import ReceiptPreview from '../../components/ReceiptPreview/ReceiptPreview';
import Totals from '../../components/Totals/Totals';
import PrinterControls from '../../components/PrinterControls/PrinterControls';
import { fetchProductData, saveProductData, loadProductData, needsProductDataRefresh } from '../../utils/productData';
import { loadDeliveryCharge, saveDeliveryCharge } from '../../utils/deliveryCharge';
import { loadDiscount, saveDiscount } from '../../utils/discount';
// import { loadTelegramConfig } from '../../utils/telegram';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faPrint } from '@fortawesome/free-solid-svg-icons';
import './ReceiptPage.css';

interface ReceiptPageProps {
  initialItems?: Item[];
  storeDetails: StoreDetails;
}

const ReceiptPage: React.FC<ReceiptPageProps> = ({
  initialItems = [],
  storeDetails
}) => {
  // Delivery charge state
  const [deliveryCharge, setDeliveryCharge] = useState<DeliveryCharge>({
    label: 'Delivery Charge',
    amount: 60
  });
  
  // Discount state
  const [discount, setDiscount] = useState<Discount>({
    label: 'Discount',
    amount: 0
  });
  
  // Load delivery charge and discount from local storage on component mount
  useEffect(() => {
    const savedDeliveryCharge = loadDeliveryCharge();
    if (savedDeliveryCharge) {
      setDeliveryCharge(savedDeliveryCharge);
    }
    
    const savedDiscount = loadDiscount();
    if (savedDiscount) {
      setDiscount(savedDiscount);
    }
  }, []);
  
  // Handle delivery charge change
  const handleDeliveryChargeChange = (newCharge: DeliveryCharge) => {
    // Update UI state only; persistence will occur on print
    setDeliveryCharge(newCharge);
  };
  
  // Handle discount change
  const handleDiscountChange = (newDiscount: Discount) => {
    // Update UI state only; persistence will occur on print
    setDiscount(newDiscount);
  };
  // Items state
  const [items, setItems] = useState<Item[]>(initialItems);
  const [, setIsLoadingProducts] = useState<boolean>(false);
  
  // Add item handler
  const handleAddItem = (item: Item) => {
    setItems(prevItems => [...prevItems, item]);
  };

  // Remove item handler
  const handleRemoveItem = (id: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
  };
  
  // Update item handler
  const handleUpdateItem = (id: string, updates: Partial<Item>) => {
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === id ? { ...item, ...updates } : item
      )
    );
  };
  
  // Product data update handler
  const handleProductDataUpdate = async () => {
    setIsLoadingProducts(true);
    try {
      const data = await fetchProductData();
      saveProductData(data);
    } catch (error) {
      console.error('Error loading product data:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };
  
  // Auto-load product data on component mount if needed
  useEffect(() => {
    const loadInitialProductData = async () => {
      // Check if we have product data in local storage
      const existingData = loadProductData();
      
      // If no data or data needs refresh, load it automatically
      if (!existingData || needsProductDataRefresh()) {
        handleProductDataUpdate();
      }
    };
    
    loadInitialProductData();
  }, []);

  return (
    <div className="receipt-page">
      <div className="receipt-page-header">
        <h1>Receipt Generator</h1>
        <p className="receipt-page-subtitle">Create and print professional receipts for your customers</p>
      </div>
      
      <div className="receipt-page-content">        
        <section className="receipt-section">
          <h2>
            <FontAwesomeIcon icon={faPlusCircle} />
            Add Items
          </h2>
          <ItemForm onAddItem={handleAddItem} />
        </section>
        
        <section className="receipt-section receipt-preview-section">
          <ReceiptPreview 
            items={items} 
            onRemoveItem={handleRemoveItem}
            onUpdateItem={handleUpdateItem}
            storeDetails={storeDetails}
            deliveryCharge={deliveryCharge}
            discount={discount}
          />
          
          <div className="receipt-totals-wrapper">
            <Totals 
              items={items} 
              deliveryCharge={deliveryCharge} 
              discount={discount}
              onDeliveryChargeChange={handleDeliveryChargeChange}
              onDiscountChange={handleDiscountChange}
            />
          </div>
        </section>
        
        <section className="receipt-section">
          <h2>
            <FontAwesomeIcon icon={faPrint} />
            Print Options
          </h2>
          <PrinterControls 
            items={items} 
            storeDetails={storeDetails} 
            deliveryCharge={deliveryCharge} 
            discount={discount}
            disabled={items.length === 0}
            onBeforePrint={async () => {
              // Persist the latest charges right before printing
              try {
                saveDeliveryCharge(deliveryCharge);
                saveDiscount(discount);
              } catch (e) {
                console.error('Failed to persist charges before print:', e);
              }
            }} 
          />
        </section>
      </div>
      
      <div className="receipt-page-footer">
        <p>Need help? Check the <a href="/docs">documentation</a> or <a href="/support">contact support</a></p>
      </div>
    </div>
  );
};

export default ReceiptPage;
